import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAccounts } from '../contexts/AccountContext';
import { useTransactions } from '../contexts/TransactionContext';
import { useTheme } from '../contexts/ThemeContext';
import { useCurrency } from '../contexts/CurrencyContext';
import {
  Plus,
  Edit2,
  Trash2,
  ArrowRightLeft,
  Wallet,
  CreditCard,
  Building2,
  ChevronRight,
  Clock,
  History,
  X,
  ShieldCheck,
  TrendingUp,
  Layers,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import NetWorthDashboard from '../components/NetWorthDashboard';
import { calculateNetWorthIntelligence, isLiabilityType } from 'shared/utils/netWorthIntelligence';

const ACCOUNT_TYPES = [
  { value: 'cash', label: 'Cash & Wallet', icon: Wallet, color: '#10B981', category: 'asset' },
  { value: 'bank', label: 'Bank Account', icon: Building2, color: '#5B9FE3', category: 'asset' },
  { value: 'savings', label: 'Savings & Deposits', icon: ShieldCheck, color: '#6366F1', category: 'asset' },
  { value: 'investment', label: 'Investments & Stocks', icon: TrendingUp, color: '#8B5CF6', category: 'asset' },
  { value: 'card', label: 'Credit Card', icon: CreditCard, color: '#E8544F', category: 'liability' },
  { value: 'loan', label: 'Loan / Mortgage', icon: ArrowRightLeft, color: '#F97316', category: 'liability' },
];

const Accounts = () => {
  const navigate = useNavigate();
  const { accounts, addAccount, updateAccount, deleteAccount, loading } = useAccounts();
  const { allTransactions, addTransaction } = useTransactions();
  const { theme, isDark } = useTheme();
  const { formatCurrency, displayCurrency, getCurrencyInfo } = useCurrency();
  const currencyInfo = getCurrencyInfo(displayCurrency);
  const [showAccountDialog, setShowAccountDialog] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showTransferHistory, setShowTransferHistory] = useState(false);
  const [expandedAccount, setExpandedAccount] = useState(null);
  const [editingAccount, setEditingAccount] = useState(null);
  const [deletingAccount, setDeletingAccount] = useState(null);
  const [accountForm, setAccountForm] = useState({ name: '', type: 'cash', initialBalance: '0' });
  const [transferForm, setTransferForm] = useState({ fromAccount: '', toAccount: '', amount: '', description: '' });
  const [activeTab, setActiveTab] = useState('all'); // 'all' | 'assets' | 'liabilities'

  // Comprehensive Net Worth & Live Balances Intelligence
  const netWorthData = useMemo(() => {
    return calculateNetWorthIntelligence(accounts, allTransactions);
  }, [accounts, allTransactions]);

  const accountBalances = netWorthData.allBalances;

  // Backward-compatible totals object
  const totals = useMemo(() => ({
    assets: netWorthData.totalAssets,
    liabilities: netWorthData.totalLiabilities,
    total: netWorthData.netWorth
  }), [netWorthData]);

  // Get recent transactions for an account (last 3)
  const getRecentTransactions = (accountName) => {
    return allTransactions
      .filter(t => t.account === accountName || t.toAccount === accountName)
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 3);
  };

  // Get all transfer transactions for history
  const transferHistory = useMemo(() => {
    return allTransactions
      .filter(t => t.type === 'transfer')
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [allTransactions]);

  // Navigate to transactions page with account filter
  const handleViewAccountTransactions = (accountName) => {
    // Store the filter in sessionStorage so Transactions page can pick it up
    sessionStorage.setItem('accountFilter', accountName);
    navigate('/transactions');
  };

  const handleOpenAddDialog = () => {
    setEditingAccount(null);
    setAccountForm({ name: '', type: 'cash', initialBalance: '0' });
    setShowAccountDialog(true);
  };

  const handleOpenEditDialog = (account) => {
    setEditingAccount(account);
    setAccountForm({
      name: account.name,
      type: account.type,
      initialBalance: account.initialBalance?.toString() || '0'
    });
    setShowAccountDialog(true);
  };

  const handleSaveAccount = async () => {
    if (!accountForm.name.trim()) {
      toast.error('Please enter an account name');
      return;
    }

    try {
      const accountData = {
        name: accountForm.name.trim(),
        type: accountForm.type,
        initialBalance: parseFloat(accountForm.initialBalance) || 0
      };

      if (editingAccount) {
        await updateAccount(editingAccount.id, accountData);
        toast.success('Account updated!');
      } else {
        await addAccount(accountData);
        toast.success('Account added!');
      }

      setShowAccountDialog(false);
      setEditingAccount(null);
      setAccountForm({ name: '', type: 'cash', initialBalance: '0' });
    } catch (error) {
      console.error('Failed to save account:', error);
      toast.error('Failed to save account');
    }
  };

  const handleDeleteAccount = async () => {
    if (!deletingAccount) return;

    try {
      await deleteAccount(deletingAccount.id);
      toast.success('Account deleted!');
      setShowDeleteDialog(false);
      setDeletingAccount(null);
    } catch (error) {
      console.error('Failed to delete account:', error);
      toast.error('Failed to delete account');
    }
  };

  const handleTransfer = async () => {
    if (!transferForm.fromAccount || !transferForm.toAccount) {
      toast.error('Please select both accounts');
      return;
    }

    if (transferForm.fromAccount === transferForm.toAccount) {
      toast.error('Cannot transfer to the same account');
      return;
    }

    if (!transferForm.amount || parseFloat(transferForm.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    try {
      const amount = parseFloat(transferForm.amount);
      const description = transferForm.description.trim() || `Transfer from ${transferForm.fromAccount} to ${transferForm.toAccount}`;

      // Create transfer transaction (outgoing from fromAccount)
      await addTransaction({
        type: 'transfer',
        amount,
        category: 'Transfer',
        categoryIcon: '🔄',
        account: transferForm.fromAccount,
        toAccount: transferForm.toAccount,
        date: new Date(),
        description
      });

      toast.success('Transfer completed!');
      setShowTransferDialog(false);
      setTransferForm({ fromAccount: '', toAccount: '', amount: '', description: '' });
    } catch (error) {
      console.error('Failed to transfer:', error);
      toast.error('Failed to complete transfer');
    }
  };

  const getAccountIcon = (type) => {
    const accountType = ACCOUNT_TYPES.find(t => t.value === type);
    return accountType || ACCOUNT_TYPES[0];
  };

  // Filter accounts based on selected tab
  const visibleAccounts = useMemo(() => {
    if (activeTab === 'assets') return accounts.filter(a => !isLiabilityType(a.type));
    if (activeTab === 'liabilities') return accounts.filter(a => isLiabilityType(a.type));
    return accounts;
  }, [accounts, activeTab]);

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold" style={{ color: theme.text }} data-testid="accounts-title">Accounts & Net Worth</h1>
          <div className="flex gap-2">
            {transferHistory.length > 0 && (
              <Button
                onClick={() => setShowTransferHistory(true)}
                variant="outline"
                className="flex items-center gap-2"
                data-testid="transfer-history-btn"
                style={{ borderColor: theme.divider, color: theme.text }}
              >
                <History className="w-4 h-4" />
                <span className="hidden sm:inline">History</span>
              </Button>
            )}
            <Button
              onClick={() => setShowTransferDialog(true)}
              variant="outline"
              className="flex items-center gap-2"
              disabled={accounts.length < 2}
              data-testid="transfer-btn"
              style={{ borderColor: theme.divider, color: theme.text }}
            >
              <ArrowRightLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Transfer</span>
            </Button>
            <Button
              onClick={handleOpenAddDialog}
              className="flex items-center gap-2"
              style={{ backgroundColor: theme.accent, color: 'white' }}
              data-testid="add-account-btn"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">Add Account</span>
            </Button>
          </div>
        </div>

        {/* Net Worth Dashboard (KPIs, 6-Month Trajectory, Debt Health) */}
        <NetWorthDashboard netWorthData={netWorthData} />

        {/* Account Classification Filter Tabs */}
        {accounts.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex p-1 rounded-xl" style={{ backgroundColor: isDark ? theme.inputBackground : '#E2E8F0' }}>
              <button
                type="button"
                onClick={() => setActiveTab('all')}
                className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  activeTab === 'all' ? 'shadow-sm' : 'opacity-70 hover:opacity-100'
                }`}
                style={{
                  backgroundColor: activeTab === 'all' ? theme.cardBackground : 'transparent',
                  color: activeTab === 'all' ? theme.text : theme.textSecondary,
                }}
              >
                All Accounts ({accounts.length})
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('assets')}
                className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  activeTab === 'assets' ? 'shadow-sm' : 'opacity-70 hover:opacity-100'
                }`}
                style={{
                  backgroundColor: activeTab === 'assets' ? theme.cardBackground : 'transparent',
                  color: activeTab === 'assets' ? '#10B981' : theme.textSecondary,
                }}
              >
                Assets ({netWorthData.assetAccounts.length})
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('liabilities')}
                className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  activeTab === 'liabilities' ? 'shadow-sm' : 'opacity-70 hover:opacity-100'
                }`}
                style={{
                  backgroundColor: activeTab === 'liabilities' ? theme.cardBackground : 'transparent',
                  color: activeTab === 'liabilities' ? '#EF4444' : theme.textSecondary,
                }}
              >
                Liabilities ({netWorthData.liabilityAccounts.length})
              </button>
            </div>
            <span className="text-xs font-medium" style={{ color: theme.textSecondary }}>
              {activeTab === 'all'
                ? `Total Net: ${formatCurrency(netWorthData.netWorth)}`
                : activeTab === 'assets'
                ? `Total Assets: ${formatCurrency(netWorthData.totalAssets)}`
                : `Total Liabilities: ${formatCurrency(netWorthData.totalLiabilities)}`}
            </span>
          </div>
        )}

        {/* Accounts List */}
        {loading ? (
          <div className="text-center py-12">
            <p style={{ color: theme.textSecondary }}>Loading accounts...</p>
          </div>
        ) : accounts.length === 0 ? (
          <div className="text-center py-12 rounded-lg" style={{ backgroundColor: theme.cardBackground, boxShadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.08)', border: `1px solid ${theme.divider}` }}>
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ backgroundColor: isDark ? theme.inputBackground : '#F8F9FA' }}>
              <Wallet className="w-8 h-8" style={{ color: theme.textSecondary }} />
            </div>
            <h3 className="text-lg font-semibold mb-2" style={{ color: theme.textSecondary }}>No accounts yet</h3>
            <p className="text-sm mb-4" style={{ color: theme.textTertiary }}>Add your first account to start tracking balances</p>
            <Button
              onClick={handleOpenAddDialog}
              style={{ backgroundColor: theme.accent, color: 'white' }}
            >
              Add Account
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {ACCOUNT_TYPES.map(({ value: typeValue, label: typeLabel }) => {
              const typeAccounts = visibleAccounts.filter(a => a.type === typeValue);
              if (typeAccounts.length === 0) return null;

              return (
                <div key={typeValue}>
                  <div className="text-sm font-medium uppercase px-3 py-2 mb-2 rounded" style={{ backgroundColor: isDark ? theme.inputBackground : '#E9ECEF', color: theme.textSecondary }}>
                    {typeLabel}
                  </div>
                  <div className="space-y-2">
                    {typeAccounts.map(account => {
                      const { icon: Icon, color } = getAccountIcon(account.type);
                      const balance = accountBalances[account.name] || 0;
                      const recentTxns = getRecentTransactions(account.name);
                      const isExpanded = expandedAccount === account.id;
                      const isLiability = isLiabilityType(account.type);

                      return (
                        <div
                          key={account.id}
                          className="rounded-lg overflow-hidden transition-shadow duration-200"
                          style={{ backgroundColor: theme.cardBackground, boxShadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.08)', border: `1px solid ${theme.divider}` }}
                          data-testid={`account-card-${account.id}`}
                        >
                          {/* Main Account Row */}
                          <div 
                            className="p-4 flex items-center justify-between group cursor-pointer transition-colors"
                            style={{ backgroundColor: 'transparent' }}
                            onClick={() => setExpandedAccount(isExpanded ? null : account.id)}
                          >
                            <div className="flex items-center gap-3">
                              <div
                                className="w-12 h-12 rounded-full flex items-center justify-center"
                                style={{ backgroundColor: `${color}20` }}
                              >
                                <Icon className="w-6 h-6" style={{ color }} />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-semibold" style={{ color: theme.text }}>{account.name}</p>
                                  <span
                                    className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                                    style={{
                                      backgroundColor: isLiability ? '#EF444418' : '#10B98118',
                                      color: isLiability ? '#EF4444' : '#10B981',
                                    }}
                                  >
                                    {isLiability ? 'Liability' : 'Asset'}
                                  </span>
                                </div>
                                <p className="text-sm" style={{ color: theme.textSecondary }}>
                                  {ACCOUNT_TYPES.find(t => t.value === account.type)?.label || account.type}
                                </p>
                              </div>
                            </div>

                            <div className="flex items-center gap-3">
                              <div className="text-right">
                                <p className="text-lg font-bold" style={{ color: balance >= 0 ? theme.income : theme.accent }}>
                                  {formatCurrency(balance)}
                                </p>
                                {recentTxns.length > 0 && (
                                  <p className="text-xs" style={{ color: theme.textSecondary }}>
                                    {recentTxns.length} recent txn{recentTxns.length > 1 ? 's' : ''}
                                  </p>
                                )}
                              </div>
                              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleOpenEditDialog(account); }}
                                  className="p-2 rounded-lg transition-colors"
                                  title="Edit account"
                                  data-testid={`edit-account-${account.id}`}
                                >
                                  <Edit2 className="w-4 h-4" style={{ color: theme.textSecondary }} />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDeletingAccount(account);
                                    setShowDeleteDialog(true);
                                  }}
                                  className="p-2 rounded-lg transition-colors"
                                  title="Delete account"
                                  data-testid={`delete-account-${account.id}`}
                                >
                                  <Trash2 className="w-4 h-4" style={{ color: theme.accent }} />
                                </button>
                              </div>
                              <ChevronRight 
                                className={`w-5 h-5 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                                style={{ color: theme.textSecondary }}
                              />
                            </div>
                          </div>

                          {/* Expanded Section - Recent Transactions */}
                          {isExpanded && (
                            <div className="border-t px-4 py-3 animate-in fade-in duration-200" style={{ backgroundColor: isDark ? theme.inputBackground : '#F8F9FA', borderColor: theme.divider }}>
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="text-sm font-medium flex items-center gap-2" style={{ color: theme.textSecondary }}>
                                  <Clock className="w-4 h-4" />
                                  Recent Transactions
                                </h4>
                                <button
                                  onClick={() => handleViewAccountTransactions(account.name)}
                                  className="text-sm font-medium flex items-center gap-1 hover:underline"
                                  style={{ color: theme.accent }}
                                  data-testid={`view-all-txns-${account.id}`}
                                >
                                  View All
                                  <ChevronRight className="w-4 h-4" />
                                </button>
                              </div>
                              
                              {recentTxns.length === 0 ? (
                                <p className="text-sm text-center py-4" style={{ color: theme.textTertiary }}>
                                  No transactions yet
                                </p>
                              ) : (
                                <div className="space-y-2">
                                  {recentTxns.map(txn => (
                                    <div 
                                      key={txn.id} 
                                      className="p-3 rounded-lg flex items-center justify-between"
                                      style={{ backgroundColor: theme.cardBackground, boxShadow: isDark ? '0 1px 3px rgba(0,0,0,0.2)' : '0 1px 3px rgba(0,0,0,0.05)' }}
                                    >
                                      <div className="flex items-center gap-2">
                                        <span className="text-lg">{txn.categoryIcon}</span>
                                        <div>
                                          <p className="text-sm font-medium" style={{ color: theme.text }}>
                                            {txn.type === 'transfer' ? `Transfer ${txn.account === account.name ? 'to ' + txn.toAccount : 'from ' + txn.account}` : txn.category}
                                          </p>
                                          <p className="text-xs" style={{ color: theme.textSecondary }}>
                                            {format(new Date(txn.date), 'MMM d, h:mm a')}
                                          </p>
                                        </div>
                                      </div>
                                      <p 
                                        className="text-sm font-bold"
                                        style={{ 
                                          color: txn.type === 'income' || (txn.type === 'transfer' && txn.toAccount === account.name) 
                                            ? theme.income 
                                            : theme.accent 
                                        }}
                                      >
                                        {txn.type === 'income' || (txn.type === 'transfer' && txn.toAccount === account.name) ? '+' : '-'}
                                        {formatCurrency(txn.amount)}
                                      </p>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Add/Edit Account Dialog */}
        <Dialog open={showAccountDialog} onOpenChange={setShowAccountDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingAccount ? 'Edit Account' : 'Add Account'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Account Name</Label>
                <Input
                  value={accountForm.name}
                  onChange={(e) => setAccountForm({ ...accountForm, name: e.target.value })}
                  placeholder="e.g., Main Checking"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Account Type</Label>
                <Select value={accountForm.type} onValueChange={(value) => setAccountForm({ ...accountForm, type: value })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ACCOUNT_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        <div className="flex items-center gap-2">
                          <type.icon className="w-4 h-4" />
                          {type.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Initial Balance ({currencyInfo?.symbol || '$'})</Label>
                <Input
                  type="number"
                  value={accountForm.initialBalance}
                  onChange={(e) => setAccountForm({ ...accountForm, initialBalance: e.target.value })}
                  placeholder="0"
                  className="mt-1"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAccountDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSaveAccount}
                style={{ backgroundColor: '#E8544F', color: 'white' }}
              >
                {editingAccount ? 'Update' : 'Add'} Account
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Transfer Dialog */}
        <Dialog open={showTransferDialog} onOpenChange={setShowTransferDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Transfer Money</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>From Account</Label>
                <Select value={transferForm.fromAccount} onValueChange={(value) => setTransferForm({ ...transferForm, fromAccount: value })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select account" />
                  </SelectTrigger>
                  <SelectContent>
                    {accounts.map(account => (
                      <SelectItem key={account.id} value={account.name}>
                        {account.name} ({formatCurrency(accountBalances[account.name] || 0)})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>To Account</Label>
                <Select value={transferForm.toAccount} onValueChange={(value) => setTransferForm({ ...transferForm, toAccount: value })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select account" />
                  </SelectTrigger>
                  <SelectContent>
                    {accounts.map(account => (
                      <SelectItem key={account.id} value={account.name}>
                        {account.name} ({formatCurrency(accountBalances[account.name] || 0)})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Amount ({currencyInfo?.symbol || '$'})</Label>
                <Input
                  type="number"
                  value={transferForm.amount}
                  onChange={(e) => setTransferForm({ ...transferForm, amount: e.target.value })}
                  placeholder="0.00"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Description (Optional)</Label>
                <Input
                  value={transferForm.description}
                  onChange={(e) => setTransferForm({ ...transferForm, description: e.target.value })}
                  placeholder="Add a note..."
                  className="mt-1"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTransferDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleTransfer}
                style={{ backgroundColor: '#E8544F', color: 'white' }}
              >
                Transfer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Account</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete <strong>{deletingAccount?.name}</strong>?
                <span className="block mt-2 text-red-600">
                  This action cannot be undone. Transactions linked to this account will remain but may show incorrect balances.
                </span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteAccount}
                style={{ backgroundColor: '#DC3545' }}
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Transfer History Dialog */}
        <Dialog open={showTransferHistory} onOpenChange={setShowTransferHistory}>
          <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2" style={{ color: theme.text }}>
                <History className="w-5 h-5" style={{ color: theme.accent }} />
                Transfer History
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto py-2">
              {transferHistory.length === 0 ? (
                <div className="text-center py-8">
                  <ArrowRightLeft className="w-12 h-12 mx-auto mb-3" style={{ color: theme.textTertiary }} />
                  <p style={{ color: theme.textSecondary }}>No transfers yet</p>
                  <p className="text-sm" style={{ color: theme.textTertiary }}>
                    Transfer money between accounts to see history here
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {transferHistory.map(transfer => (
                    <div 
                      key={transfer.id} 
                      className="p-4 rounded-lg"
                      style={{ backgroundColor: isDark ? theme.inputBackground : '#F8F9FA' }}
                      data-testid={`transfer-history-item-${transfer.id}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: isDark ? '#3D1F1F' : '#FEE2E2' }}>
                            <ArrowRightLeft className="w-4 h-4" style={{ color: theme.accent }} />
                          </div>
                          <div>
                            <p className="font-medium text-sm" style={{ color: theme.text }}>
                              {transfer.account} → {transfer.toAccount}
                            </p>
                            <p className="text-xs" style={{ color: theme.textSecondary }}>
                              {format(new Date(transfer.date), 'MMM d, yyyy • h:mm a')}
                            </p>
                          </div>
                        </div>
                        <p className="font-bold" style={{ color: theme.accent }}>
                          {formatCurrency(transfer.amount)}
                        </p>
                      </div>
                      {transfer.description && (
                        <p className="text-sm pl-10" style={{ color: theme.textSecondary }}>
                          {transfer.description}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTransferHistory(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Accounts;
